/**
 * パイプライン API クライアント
 *
 * バックエンド /api/v1/pipeline/* との通信。axios client (api) 経由で
 * Cognito JWT の付与と 401 ハンドリングを共通化している。S3 への presigned PUT
 * のみ axios を経由せず素の XMLHttpRequest で送る（Authorization ヘッダを付けない
 * ようにするのと、進捗計測のため）。
 */

import { api } from './client';
import { env } from '@/lib/env';
import type {
  PipelineJobResponse,
  PipelineJobListResponse,
  PipelineMultipartCompleteResponse,
  PipelineMultipartCompletedPart,
  PipelineMultipartUploadCreateRequest,
  PipelineMultipartUploadCreateResponse,
  PipelineStartResponse,
  PipelineSubmitRequest,
  PipelineSubmitResponse,
  PipelineUploadFailedRequest,
  PipelineUploadPresignResponse,
  PipelineUploadStatusResponse,
  PipelineUploadTarget,
  SectionSubmitRequest,
} from '@/types/pipeline';

const DEFAULT_UPLOAD_STALL_TIMEOUT_MS = 180_000;
const DEFAULT_MULTIPART_PART_ATTEMPTS = 3;
const DEFAULT_MULTIPART_PART_CONCURRENCY = 3;
const MULTIPART_UPLOAD_THRESHOLD_BYTES = 64 * 1024 * 1024;

interface UploadAudioOptions {
  stallTimeoutMs?: number;
  signal?: AbortSignal;
}

interface UploadMultipartOptions extends UploadAudioOptions {
  onProgress?: (percent: number) => void;
  partAttempts?: number;
  partConcurrency?: number;
}

function sleepWithAbort(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('S3 upload aborted'));
      return;
    }
    let timeout: number;
    const abort = () => {
      window.clearTimeout(timeout);
      reject(new Error('S3 upload aborted'));
    };
    timeout = window.setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', abort, { once: true });
  });
}

/** ジョブ投入 + Presigned URL 取得 */
export async function submitPipelineJob(
  request: PipelineSubmitRequest,
): Promise<PipelineSubmitResponse> {
  const resp = await api.post<PipelineSubmitResponse>('/pipeline/submit', request);
  return resp.data;
}

/**
 * Presigned URL に音声ファイルを PUT アップロード
 *
 * 認可: presigned URL 自体に署名が含まれるため Authorization ヘッダ等は付けない。
 * 進捗計測のため XMLHttpRequest を直接使用する。
 */
export function uploadAudioToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
  contentType?: string,
  options: UploadAudioOptions = {},
): Promise<void> {
  if (env.VITE_UI_PREVIEW || uploadUrl.startsWith('mock://')) {
    onProgress?.(100);
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const stallTimeoutMs = options.stallTimeoutMs ?? DEFAULT_UPLOAD_STALL_TIMEOUT_MS;
    let stallTimer: number | undefined;
    let settled = false;
    const abortFromSignal = () => xhr.abort();

    const clearStallTimer = () => {
      if (stallTimer !== undefined) {
        window.clearTimeout(stallTimer);
        stallTimer = undefined;
      }
    };
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearStallTimer();
      options.signal?.removeEventListener('abort', abortFromSignal);
      fn();
    };
    const resetStallTimer = () => {
      clearStallTimer();
      stallTimer = window.setTimeout(() => {
        xhr.abort();
        finish(() => reject(new Error('S3 upload stalled: no progress received')));
      }, stallTimeoutMs);
    };

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType || file.type || 'audio/wav');
    if (options.signal?.aborted) {
      finish(() => reject(new Error('S3 upload aborted')));
      return;
    }
    options.signal?.addEventListener('abort', abortFromSignal, { once: true });

    xhr.upload.onprogress = (e) => {
      resetStallTimer();
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        finish(resolve);
      } else {
        finish(() => reject(new Error(`S3 upload failed: HTTP ${xhr.status}`)));
      }
    };

    xhr.onerror = () => finish(() => reject(new Error('S3 upload network error')));
    xhr.onabort = () => finish(() => reject(new Error('S3 upload aborted')));
    resetStallTimer();
    xhr.send(file);
  });
}

function uploadPartToS3(
  uploadUrl: string,
  blob: Blob,
  onProgress?: (loaded: number) => void,
  options: UploadAudioOptions = {},
): Promise<string> {
  if (env.VITE_UI_PREVIEW || uploadUrl.startsWith('mock://')) {
    return Promise.resolve('"mock-etag"');
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const stallTimeoutMs = options.stallTimeoutMs ?? DEFAULT_UPLOAD_STALL_TIMEOUT_MS;
    let stallTimer: number | undefined;
    let settled = false;
    const abortFromSignal = () => xhr.abort();

    const clearStallTimer = () => {
      if (stallTimer !== undefined) {
        window.clearTimeout(stallTimer);
        stallTimer = undefined;
      }
    };
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearStallTimer();
      options.signal?.removeEventListener('abort', abortFromSignal);
      fn();
    };
    const resetStallTimer = () => {
      clearStallTimer();
      stallTimer = window.setTimeout(() => {
        xhr.abort();
        finish(() => reject(new Error('S3 multipart upload stalled: no progress received')));
      }, stallTimeoutMs);
    };

    xhr.open('PUT', uploadUrl);
    if (options.signal?.aborted) {
      finish(() => reject(new Error('S3 multipart upload aborted')));
      return;
    }
    options.signal?.addEventListener('abort', abortFromSignal, { once: true });
    xhr.upload.onprogress = (e) => {
      resetStallTimer();
      if (e.lengthComputable) onProgress?.(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag');
        if (etag) {
          finish(() => resolve(etag));
        } else {
          finish(() => reject(new Error('S3 multipart upload failed: missing ETag')));
        }
      } else {
        finish(() => reject(new Error(`S3 multipart upload failed: HTTP ${xhr.status}`)));
      }
    };
    xhr.onerror = () => finish(() => reject(new Error('S3 multipart upload network error')));
    xhr.onabort = () => finish(() => reject(new Error('S3 multipart upload aborted')));
    resetStallTimer();
    xhr.send(blob);
  });
}

/** 大容量ファイルはS3 multipart uploadで分割PUTする */
export async function uploadAudioMultipartToS3(
  jobId: string,
  target: PipelineMultipartUploadCreateRequest,
  file: File,
  options: UploadMultipartOptions = {},
): Promise<PipelineMultipartCompleteResponse> {
  if (options.signal?.aborted) throw new Error('S3 multipart upload aborted');
  const started = await api.post<PipelineMultipartUploadCreateResponse>(
    `/pipeline/jobs/${jobId}/uploads/multipart`,
    target,
  );
  const upload = started.data;
  const completedParts: PipelineMultipartCompletedPart[] = [];
  const partProgress = new Map<number, number>();
  const uploadAbortController = new AbortController();
  const abortFromExternalSignal = () => uploadAbortController.abort();
  if (options.signal?.aborted) uploadAbortController.abort();
  options.signal?.addEventListener('abort', abortFromExternalSignal, { once: true });
  const partOptions: UploadAudioOptions = {
    stallTimeoutMs: options.stallTimeoutMs,
    signal: uploadAbortController.signal,
  };
  const updateProgress = (partNumber: number, loaded: number) => {
    partProgress.set(partNumber, loaded);
    const totalLoaded = Array.from(partProgress.values()).reduce((sum, value) => sum + value, 0);
    options.onProgress?.(Math.min(100, Math.round((totalLoaded / file.size) * 100)));
  };
  const uploadPartWithRetry = async (part: PipelineMultipartUploadCreateResponse['parts'][number]) => {
    const start = (part.part_number - 1) * upload.part_size;
    const end = Math.min(start + upload.part_size, file.size);
    const blob = file.slice(start, end);
    let etag: string | null = null;
    let lastError: unknown;
    const attempts = options.partAttempts ?? DEFAULT_MULTIPART_PART_ATTEMPTS;
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      if (partOptions.signal?.aborted) throw new Error('S3 multipart upload aborted');
      try {
        etag = await uploadPartToS3(part.upload_url, blob, (loaded) => {
          updateProgress(part.part_number, loaded);
        }, partOptions);
        break;
      } catch (err) {
        lastError = err;
        partProgress.delete(part.part_number);
        if (attempt < attempts) {
          await sleepWithAbort(attempt * 1000, partOptions.signal);
        }
      }
    }
    if (!etag) throw lastError;
    completedParts.push({ part_number: part.part_number, etag });
    updateProgress(part.part_number, blob.size);
  };

  try {
    const partConcurrency = Math.max(
      1,
      Math.min(options.partConcurrency ?? DEFAULT_MULTIPART_PART_CONCURRENCY, upload.parts.length),
    );
    let nextPartIndex = 0;
    const uploadNextPart = async () => {
      while (!partOptions.signal?.aborted) {
        const part = upload.parts[nextPartIndex];
        nextPartIndex += 1;
        if (!part) return;
        await uploadPartWithRetry(part);
      }
    };
    await Promise.all(Array.from({ length: partConcurrency }, uploadNextPart));
    if (partOptions.signal?.aborted) throw new Error('S3 multipart upload aborted');
    const completed = await api.post<PipelineMultipartCompleteResponse>(
      `/pipeline/jobs/${jobId}/uploads/multipart/complete`,
      {
        role: target.role,
        filename: target.filename,
        upload_id: upload.upload_id,
        parts: completedParts.sort((a, b) => a.part_number - b.part_number),
      },
    );
    options.onProgress?.(100);
    return completed.data;
  } catch (err) {
    uploadAbortController.abort();
    await api.post(`/pipeline/jobs/${jobId}/uploads/multipart/abort`, {
      role: target.role,
      filename: target.filename,
      upload_id: upload.upload_id,
    }).catch(() => undefined);
    throw err;
  } finally {
    options.signal?.removeEventListener('abort', abortFromExternalSignal);
  }
}

export function shouldUseMultipartUpload(file: File): boolean {
  return file.size >= MULTIPART_UPLOAD_THRESHOLD_BYTES;
}

/** パイプライン処理開始 */
export async function startPipeline(jobId: string): Promise<PipelineStartResponse> {
  const resp = await api.post<PipelineStartResponse>(`/pipeline/jobs/${jobId}/start`);
  return resp.data;
}

/** ジョブ状態取得（ポーリング用） */
export async function getJobStatus(jobId: string): Promise<PipelineJobResponse> {
  const resp = await api.get<PipelineJobResponse>(`/pipeline/jobs/${jobId}`);
  return resp.data;
}

/** ジョブの期待ファイルごとのアップロード状態 */
export async function getUploadStatus(jobId: string): Promise<PipelineUploadStatusResponse> {
  const resp = await api.get<PipelineUploadStatusResponse>(`/pipeline/jobs/${jobId}/uploads`);
  return resp.data;
}

/** アップロード中 heartbeat */
export async function sendUploadHeartbeat(jobId: string): Promise<void> {
  await api.post(`/pipeline/jobs/${jobId}/uploads/heartbeat`);
}

/** アップロード失敗/中断を backend に記録 */
export async function reportUploadFailed(
  jobId: string,
  request: PipelineUploadFailedRequest,
): Promise<void> {
  await api.post(`/pipeline/jobs/${jobId}/uploads/failed`, request);
}

/** beforeunload 用 best-effort 失敗通知 */
export function beaconUploadFailed(jobId: string, request: PipelineUploadFailedRequest): void {
  const token = sessionStorage.getItem('access_token') ?? localStorage.getItem('access_token');
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '');
  const url = `${base}/pipeline/jobs/${encodeURIComponent(jobId)}/uploads/failed`;
  const payload = JSON.stringify(request);
  if (navigator.sendBeacon && !token) {
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
    return;
  }
  void fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
}

/** 不足ファイル再アップロード用 Presigned URL を取得 */
export async function presignMissingUploads(
  jobId: string,
  items: PipelineUploadTarget[],
): Promise<PipelineUploadPresignResponse> {
  const resp = await api.post<PipelineUploadPresignResponse>(
    `/pipeline/jobs/${jobId}/uploads/presign`,
    { items },
  );
  return resp.data;
}

/** ジョブ一覧 */
export async function listJobs(clientName?: string): Promise<PipelineJobListResponse> {
  const params = clientName ? { client_name: clientName } : undefined;
  const resp = await api.get<PipelineJobListResponse>('/pipeline/jobs', { params });
  return resp.data;
}

/** 文字起こし結果（Gemini 補正済み JSON） */
export async function getTranscription(jobId: string): Promise<Record<string, unknown>> {
  const resp = await api.get<Record<string, unknown>>(`/pipeline/jobs/${jobId}/transcription`);
  return resp.data;
}

/** 評価結果（direct_evaluation.json） */
export async function getEvaluation(jobId: string): Promise<Record<string, unknown>> {
  const resp = await api.get<Record<string, unknown>>(`/pipeline/jobs/${jobId}/evaluation`);
  return resp.data;
}

/** 失敗ジョブのリトライ */
export async function retryJob(jobId: string): Promise<PipelineStartResponse> {
  const resp = await api.post<PipelineStartResponse>(`/pipeline/jobs/${jobId}/retry`);
  return resp.data;
}

/** セクション設定を確定して評価開始 */
export async function submitSections(
  jobId: string,
  sections: SectionSubmitRequest['sections'],
): Promise<PipelineStartResponse> {
  const resp = await api.post<PipelineStartResponse>(`/pipeline/jobs/${jobId}/sections`, {
    sections,
  });
  return resp.data;
}
